import React, { useState, useEffect } from "react";
import {
  Table,
  Typography,
  Button,
  Breadcrumb,
  Card,
  Space,
  Input,
  Modal,
  Form,
  message,
  Tag,
  Select,
  Tooltip,
  Badge,
  Tabs,
  Row,
  Col,
  Statistic,
  Segmented,
  Divider,
  Empty,
  Spin,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import {
  HomeOutlined,
  SearchOutlined,
  EditOutlined,
  TeamOutlined,
  CheckOutlined,
  CloseOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import { supabase } from "../lib/supabase";
import { trackFeatureUsage } from "../lib/posthog";
import {
  getUsers,
  linkUserToCompany,
  unlinkUserFromCompany,
} from "../services/adminUsers";
import { useNavigate } from "react-router-dom";
import { AdminNavBar } from "../components/AdminNavBar";
import { Invoice } from "../types/invoice";
import { PicqerIntegration } from "../components/PicqerIntegration";
import AdminPicqerIntegrationSection from "../components/AdminPicqerIntegrationSection";

const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const Container = styled.div`
  padding: 24px;
`;

// Add compact styles for the table
const CompactTable = styled.div`
  .compact-table {
    .ant-table {
      font-size: 13px;
    }

    .ant-table-thead > tr > th {
      padding: 8px 16px;
    }

    .ant-table-tbody > tr > td {
      padding: 4px 16px;
    }

    .ant-table-cell {
      white-space: nowrap;
    }

    .ant-tag {
      margin-right: 0;
      font-size: 11px;
      padding: 0 4px;
      line-height: 18px;
    }

    .ant-btn {
      font-size: 12px;
      padding: 0 8px;
      height: 24px;
    }

    .ant-space {
      gap: 4px !important;
    }
  }
`;

const ApprovalCard = styled(Card)`
  margin-bottom: 16px;
  border-left: 3px solid #1890ff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .ant-card-body {
    padding: 16px;
  }

  .company-name {
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
  }

  .company-id {
    margin-left: 8px;
    font-size: 13px;
    color: #8c8c8c;
    font-weight: normal;
  }

  .company-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    margin-bottom: 12px;
  }

  .company-meta-item {
    display: flex;
    align-items: center;
  }

  .meta-label {
    font-weight: 500;
    margin-right: 4px;
    color: rgba(0, 0, 0, 0.45);
  }

  .approval-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
`;

// Add Dashboard Card component
const DashboardCard = styled(Card)`
  border-radius: 8px;
  margin-bottom: 24px;

  .ant-card-head {
    border-bottom: 0;
    padding-bottom: 0;
  }

  .ant-statistic-title {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.65);
  }

  .ant-statistic-content {
    font-size: 24px;
    color: #1890ff;
  }
`;

// Define interfaces for company and user
interface Company {
  id: number;
  created_at: string;
  name: string;
  status: "rejected" | "unapproved" | "pending" | "approved" | "deleted" | null;
  country: string | null;
  city: string | null;
  postal_code: string | null;
  vat_number: string | null;
  eori_number: string | null;
  coc_number: number | null;
  article_23: boolean | null;
  phone_number: string | null;
  created_by_user_id: string | null;
  street: string | null;
  house_number: string | null;
  users?: User[];
  moneybird_contact_id?: string;
  pricing_payment_term?: number;
  // Picqer integration fields
  picqer_api_key?: string;
  picqer_fulfilment_customer_id?: number;
  picqer_enabled?: boolean;
}

interface PricingPaymentTerm {
  id: number;
  days: number;
  interest_vat_import_duties: number;
  created_at: string;
}

interface User {
  id: string;
  email: string;
  created_at: string;
  user_metadata: {
    first_name?: string;
    last_name?: string;
  };
}

// Add pagination interface
interface PaginationParams {
  page: number;
  pageSize: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: "ascend" | "descend";
}

interface CompanyStatusCount {
  status: string;
  count: number;
}

// Booking interface
interface Booking {
  id: number;
  reference: string;
  status: string | null;
  eta: string | null;
  created_at: string;
  transport_type: number | null;
  booking_type: number | null;
  company_id: number | null;
  origin_port: number | null;
  destination_port: number | null;
  origin_pickup_date: string | null;

  // Related info when included in query
  transport_type_info?: { name: string; abbreviation: string } | null;
  booking_type_info?: { name: string } | null;
  origin_port_info?: { name: string; country: string | null } | null;
  destination_port_info?: { name: string; country: string | null } | null;
}

const CompaniesAdmin: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [pendingCompanies, setPendingCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewUsersModalVisible, setViewUsersModalVisible] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("pending");
  const [statusCounts, setStatusCounts] = useState<CompanyStatusCount[]>([]);
  const [pendingFilter, setPendingFilter] = useState<string>("pending");
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchUserText, setSearchUserText] = useState("");
  const [userSearchTimeout, setUserSearchTimeout] =
    useState<NodeJS.Timeout | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [linkingUser, setLinkingUser] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [totalCompaniesCount, setTotalCompaniesCount] = useState(0);
  const [paymentTerms, setPaymentTerms] = useState<PricingPaymentTerm[]>([]);
  const [companyInvoices, setCompanyInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceStats, setInvoiceStats] = useState({
    totalRevenue: 0,
    outstandingBalance: 0,
    invoiceCount: 0,
  });
  const [companyInvoiceStats, setCompanyInvoiceStats] = useState<
    Record<
      number,
      { invoiceCount: number; totalRevenue: number; outstandingBalance: number }
    >
  >({});

  // Add pagination and sorting state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<
    "ascend" | "descend" | undefined
  >();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [filters, setFilters] = useState<Record<string, any>>({});

  const navigate = useNavigate();

  // Fetch payment terms
  const fetchPaymentTerms = async () => {
    try {
      const { data, error } = await supabase
        .from("pricing_payment_terms")
        .select("*")
        .order("days", { ascending: true });

      if (error) throw error;
      setPaymentTerms(data || []);
    } catch (error) {
      console.error("Error fetching payment terms:", error);
    }
  };

  // Fetch company invoices and calculate stats
  const fetchCompanyInvoices = async (companyId: number) => {
    try {
      setInvoicesLoading(true);

      const { data, error } = await supabase
        .from("booking_sales_invoice")
        .select(
          `
          *,
          lines:booking_sales_invoice_line(*),
          booking:bookings(
            id,
            reference,
            transport_type_info:booking_transport_types(*)
          )
        `
        )
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const invoices = data || [];
      setCompanyInvoices(invoices);

      // Calculate statistics
      let totalRevenue = 0;
      let outstandingBalance = 0;

      invoices.forEach((invoice: any) => {
        const invoiceTotal =
          invoice.lines?.reduce((sum: number, line: any) => {
            return (
              sum +
              line.quantity * line.unit_price * (1 + line.vat_percentage / 100)
            );
          }, 0) || 0;

        totalRevenue += invoiceTotal;

        // If invoice is not paid, add to outstanding balance
        if (invoice.moneybird_payment_status !== "paid") {
          outstandingBalance += invoiceTotal;
        }
      });

      setInvoiceStats({
        totalRevenue,
        outstandingBalance,
        invoiceCount: invoices.length,
      });
    } catch (error) {
      console.error("Error fetching company invoices:", error);
    } finally {
      setInvoicesLoading(false);
    }
  };

  // Fetch invoice statistics for all companies
  const fetchCompanyInvoiceStats = async () => {
    try {
      const { data, error } = await supabase.from("booking_sales_invoice")
        .select(`
          company_id,
          moneybird_payment_status,
          lines:booking_sales_invoice_line(
            quantity,
            unit_price,
            vat_percentage
          )
        `);

      if (error) throw error;

      const stats: Record<
        number,
        {
          invoiceCount: number;
          totalRevenue: number;
          outstandingBalance: number;
        }
      > = {};

      (data || []).forEach((invoice: any) => {
        if (!invoice.company_id) return;

        if (!stats[invoice.company_id]) {
          stats[invoice.company_id] = {
            invoiceCount: 0,
            totalRevenue: 0,
            outstandingBalance: 0,
          };
        }

        stats[invoice.company_id].invoiceCount++;

        // Calculate invoice total
        const invoiceTotal =
          invoice.lines?.reduce((sum: number, line: any) => {
            return (
              sum +
              line.quantity * line.unit_price * (1 + line.vat_percentage / 100)
            );
          }, 0) || 0;

        // Add to total revenue
        stats[invoice.company_id].totalRevenue += invoiceTotal;

        // If not paid, add to outstanding balance
        if (invoice.moneybird_payment_status !== "paid") {
          stats[invoice.company_id].outstandingBalance += invoiceTotal;
        }
      });

      setCompanyInvoiceStats(stats);
    } catch (error) {
      console.error("Error fetching company invoice stats:", error);
    }
  };

  // Fetch status counts
  const fetchStatusCounts = async () => {
    try {
      const { data, error } = await supabase
        .from("company")
        .select("status")
        .then((result) => {
          // Process the results to count by status
          const counts: Record<string, number> = {};

          if (result.data) {
            result.data.forEach((company) => {
              const status = company.status || "unknown";
              counts[status] = (counts[status] || 0) + 1;
            });
          }

          return {
            data: Object.entries(counts).map(([status, count]) => ({
              status,
              count,
            })),
            error: result.error,
          };
        });

      if (error) throw error;

      setStatusCounts(data || []);
    } catch (error) {
      console.error("Error fetching status counts:", error);
    }
  };

  // Fetch pending approval companies
  const fetchPendingCompanies = async () => {
    try {
      setPendingLoading(true);

      // Get companies with status "pending" or "unapproved"
      let query = supabase.from("company").select("*");

      // Use the filter value directly
      query = query.eq("status", pendingFilter);

      const { data, error } = await query.order("created_at", {
        ascending: false,
      });

      if (error) throw error;

      setPendingCompanies(data || []);
      trackFeatureUsage("admin_companies_pending_view");
    } catch (error) {
      console.error("Error fetching pending companies:", error);
      message.error("Failed to fetch pending companies");
    } finally {
      setPendingLoading(false);
    }
  };

  // Fetch companies with pagination, sorting, and search
  const fetchCompanies = async (params?: Partial<PaginationParams>) => {
    try {
      setLoading(true);

      const page = params?.page || pagination.current;
      const pageSize = params?.pageSize || pagination.pageSize;
      const searchQueryParam =
        params?.searchQuery !== undefined ? params.searchQuery : searchQuery;
      const sortFieldParam = params?.sortField || sortField;
      const sortOrderParam = params?.sortOrder || sortOrder;

      // Check if we need to load all companies for filtering
      const hasPicqerFilter = Object.keys(filters).some(
        (key) =>
          key === "picqer_status" && filters[key] && filters[key].length > 0
      );

      // If we have Picqer filter, load all companies without pagination
      if (hasPicqerFilter) {
        const { data: allCompanies, error } = await supabase
          .from("company")
          .select("*, pricing_payment_term:pricing_payment_terms(*)");

        if (error) throw error;

        // Apply client-side filtering
        let filteredCompanies = (allCompanies || []) as Company[];

        // Apply Picqer filter
        if (filters.picqer_status && filters.picqer_status.length > 0) {
          filteredCompanies = filteredCompanies.filter((company) => {
            const isConnected = Boolean(
              company.picqer_enabled && company.picqer_fulfilment_customer_id
            );

            if (filters.picqer_status.includes("connected")) {
              return isConnected;
            } else if (filters.picqer_status.includes("not_connected")) {
              return !isConnected;
            }
            return true;
          });
        }

        // Apply search filter
        if (searchQueryParam) {
          filteredCompanies = filteredCompanies.filter(
            (company) =>
              company.name
                ?.toLowerCase()
                .includes(searchQueryParam.toLowerCase()) ||
              company.vat_number
                ?.toLowerCase()
                .includes(searchQueryParam.toLowerCase()) ||
              company.eori_number
                ?.toLowerCase()
                .includes(searchQueryParam.toLowerCase())
          );
        }

        // Apply sorting
        if (sortFieldParam && sortOrderParam) {
          const order = sortOrderParam === "ascend" ? 1 : -1;

          if (
            ["total_invoices", "total_revenue", "outstanding_balance"].includes(
              sortFieldParam
            )
          ) {
            filteredCompanies.sort((a, b) => {
              const statsA = companyInvoiceStats[a.id] || {
                invoiceCount: 0,
                totalRevenue: 0,
                outstandingBalance: 0,
              };
              const statsB = companyInvoiceStats[b.id] || {
                invoiceCount: 0,
                totalRevenue: 0,
                outstandingBalance: 0,
              };

              let valueA: number;
              let valueB: number;

              switch (sortFieldParam) {
                case "total_invoices":
                  valueA = statsA.invoiceCount;
                  valueB = statsB.invoiceCount;
                  break;
                case "total_revenue":
                  valueA = statsA.totalRevenue;
                  valueB = statsB.totalRevenue;
                  break;
                case "outstanding_balance":
                  valueA = statsA.outstandingBalance;
                  valueB = statsB.outstandingBalance;
                  break;
                default:
                  return 0;
              }

              return (valueA - valueB) * order;
            });
          } else {
            // Default sorting by creation date
            filteredCompanies.sort((a, b) => {
              const dateA = new Date(a.created_at).getTime();
              const dateB = new Date(b.created_at).getTime();
              return (dateA - dateB) * order;
            });
          }
        }

        // Apply pagination to filtered results
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedCompanies = filteredCompanies.slice(
          startIndex,
          endIndex
        );

        setCompanies(paginatedCompanies);
        setPagination({
          ...pagination,
          current: page,
          pageSize: pageSize,
          total: filteredCompanies.length,
        });
      } else {
        // Normal server-side pagination for other cases
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        let query = supabase
          .from("company")
          .select("*, pricing_payment_term:pricing_payment_terms(*)", {
            count: "exact",
          });

        if (searchQueryParam) {
          query = query.or(
            `name.ilike.%${searchQueryParam}%,vat_number.ilike.%${searchQueryParam}%,eori_number.ilike.%${searchQueryParam}%`
          );
        }

        if (sortFieldParam && sortOrderParam) {
          const order = sortOrderParam === "ascend" ? "asc" : "desc";
          query = query.order(sortFieldParam, { ascending: order === "asc" });
        } else {
          query = query.order("created_at", { ascending: false });
        }

        const { count, error: countError } = await query;
        if (countError) throw countError;

        query = query.range(from, to);
        const { data, error } = await query;
        if (error) throw error;

        setCompanies((data || []) as Company[]);
        setPagination({
          ...pagination,
          current: page,
          pageSize: pageSize,
          total: count || 0,
        });
      }

      trackFeatureUsage("admin_companies_view");
    } catch (error) {
      console.error("Error fetching companies:", error);
      message.error("Failed to fetch companies");
    } finally {
      setLoading(false);
    }
  };

  // Handle company status change
  const handleStatusChange = async (
    company: Company,
    newStatus: "approved" | "rejected" | "deleted"
  ) => {
    try {
      setPendingLoading(true);

      // Update company status in Supabase
      const { error } = await supabase
        .from("company")
        .update({ status: newStatus })
        .eq("id", company.id);

      if (error) throw error;

      message.success(
        `Company ${
          newStatus === "approved"
            ? "approved"
            : newStatus === "rejected"
            ? "rejected"
            : "marked as deleted"
        } successfully`
      );

      // Refresh the pending companies list
      fetchPendingCompanies();

      // If we're on the all companies tab, refresh that list too
      if (activeTab === "all") {
        fetchCompanies();
      }

      trackFeatureUsage(`admin_company_${newStatus}`);
    } catch (error) {
      console.error(
        `Error ${
          newStatus === "approved"
            ? "approving"
            : newStatus === "rejected"
            ? "rejecting"
            : "deleting"
        } company:`,
        error
      );
      message.error(
        `Failed to ${
          newStatus === "approved"
            ? "approve"
            : newStatus === "rejected"
            ? "reject"
            : "delete"
        } company`
      );
    } finally {
      setPendingLoading(false);
    }
  };

  // Fetch users for a specific company
  const fetchCompanyUsers = async (companyId: number) => {
    try {
      setLoading(true);

      // Get auth users directly from our edge function
      const { data, error } = await supabase.functions.invoke(
        "admin-users/company-users",
        {
          method: "POST",
          body: { companyId: companyId },
        }
      );

      if (error) {
        console.error("Error invoking edge function:", error);
        throw error;
      }

      console.log("Edge function response:", data);

      if (!data || !data.users || data.users.length === 0) {
        setCompanyUsers([]);
        return;
      }

      // Map the users to the expected format
      const users = data.users.map((user: User) => ({
        id: user.id,
        email: user.email || "",
        created_at: user.created_at,
        user_metadata: user.user_metadata || {},
      }));

      console.log(
        `Found ${users.length} users for company ${companyId}`,
        users
      );

      setCompanyUsers(users);
    } catch (error) {
      console.error("Error fetching company users:", error);
      message.error("Failed to fetch users for this company");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all users for linking
  const fetchUsers = async (search = "") => {
    try {
      setUsersLoading(true);

      const { data, error } = await getUsers(1, 50, search);

      if (error) {
        throw error;
      }

      setUsers(data?.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error("Failed to fetch users");
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle user search
  const handleUserSearch = (value: string) => {
    setSearchUserText(value);

    // Clear any existing timeout
    if (userSearchTimeout) {
      clearTimeout(userSearchTimeout);
    }

    // Set a new timeout to debounce the search
    const timeout = setTimeout(() => {
      fetchUsers(value);
    }, 500); // 500ms debounce

    setUserSearchTimeout(timeout);
  };

  // Link selected user to company
  const handleLinkUser = async () => {
    if (!selectedUserId || !selectedCompany) {
      message.error("Please select a user to link");
      return;
    }

    try {
      setLinkingUser(true);

      const { data, error } = await linkUserToCompany(
        selectedUserId,
        selectedCompany.id
      );

      if (error) {
        throw error;
      }

      message.success(
        data?.alreadyExists
          ? "User was already linked to this company"
          : "User linked to company successfully"
      );

      // Refresh company users
      fetchCompanyUsers(selectedCompany.id);

      // Reset selection
      setSelectedUserId(null);
      setSearchUserText("");
    } catch (error) {
      console.error("Error linking user to company:", error);
      message.error("Failed to link user to company");
    } finally {
      setLinkingUser(false);
    }
  };

  // Unlink user from company
  const handleUnlinkUser = async (userId: string) => {
    if (!selectedCompany) return;

    try {
      setLoading(true);

      const { error } = await unlinkUserFromCompany(userId, selectedCompany.id);

      if (error) {
        throw error;
      }

      message.success("User removed from company");

      // Refresh company users
      fetchCompanyUsers(selectedCompany.id);
    } catch (error) {
      console.error("Error unlinking user from company:", error);
      message.error("Failed to remove user from company");
    } finally {
      setLoading(false);
    }
  };

  // Fetch total count of all companies
  const fetchTotalCompaniesCount = async () => {
    try {
      const { count, error } = await supabase
        .from("company")
        .select("*", { count: "exact", head: true });

      if (error) throw error;

      setTotalCompaniesCount(count || 0);
    } catch (error) {
      console.error("Error fetching total companies count:", error);
    }
  };

  // Effect to fetch companies and pending companies on component mount or when dependencies change
  useEffect(() => {
    fetchCompanies();
    setPendingFilter("pending");
    fetchStatusCounts();
    fetchTotalCompaniesCount();
    fetchPaymentTerms();
    fetchCompanyInvoiceStats();

    // Check for company ID in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const companyIdParam = urlParams.get("id");

    if (companyIdParam) {
      console.log(`Found company ID in URL: ${companyIdParam}`);
      setActiveTab("all"); // Switch to "All Companies" tab
      setSearchText(companyIdParam);
      setSearchQuery(companyIdParam);
      fetchCompanies({
        page: 1,
        searchQuery: companyIdParam,
      });

      // Also try to directly fetch the company to show in edit modal
      const fetchCompanyById = async () => {
        try {
          const { data, error } = await supabase
            .from("company")
            .select("*")
            .eq("id", companyIdParam)
            .single();

          if (error) {
            console.error("Error fetching company by ID:", error);
            return;
          }

          if (data) {
            // Open edit modal with the company
            handleEditCompany(data);
          }
        } catch (error) {
          console.error("Error fetching company by ID:", error);
        }
      };

      fetchCompanyById();
    }
  }, [sortField, sortOrder]);

  // Effect to refetch pending companies when filter changes
  useEffect(() => {
    fetchPendingCompanies();
  }, [pendingFilter]);

  // Handle search input change
  const handleSearch = (value: string) => {
    setSearchText(value);

    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set a new timeout to debounce the search
    const timeout = setTimeout(() => {
      setSearchQuery(value);
      fetchCompanies({
        page: 1,
        searchQuery: value,
      });
    }, 500); // 500ms debounce

    setSearchTimeout(timeout);
  };

  // Handle table change (pagination, filters, sorter)
  const handleTableChange = (
    pagination: TablePaginationConfig,
    tableFilters: Record<string, FilterValue | null>,
    sorter: SorterResult<Company> | SorterResult<Company>[]
  ) => {
    // Handle sorting
    let newSortField: string | undefined;
    let newSortOrder: "ascend" | "descend" | undefined;

    if (!Array.isArray(sorter)) {
      newSortField = sorter.field as string;
      newSortOrder = sorter.order as "ascend" | "descend" | undefined;
    }

    // Set sort state for next query
    setSortField(newSortField);
    setSortOrder(newSortOrder);

    // Update filters state
    setFilters(tableFilters);

    // Handle pagination
    fetchCompanies({
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      sortField: newSortField,
      sortOrder: newSortOrder,
    });
  };

  // Handle company edit
  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);

    // Extract payment term ID if it's an object from the join
    let paymentTermId = company.pricing_payment_term;
    if (
      company.pricing_payment_term &&
      typeof company.pricing_payment_term === "object"
    ) {
      paymentTermId = (company.pricing_payment_term as PricingPaymentTerm).id;
    }

    form.setFieldsValue({
      ...company,
      pricing_payment_term: paymentTermId,
    });

    // Fetch company invoices when opening edit modal
    fetchCompanyInvoices(company.id);

    setEditModalVisible(true);
  };

  // View company users
  const handleViewUsers = (company: Company) => {
    setSelectedCompany(company);
    fetchCompanyUsers(company.id);
    fetchUsers();
    setViewUsersModalVisible(true);
  };

  // Handle edit form submission
  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedCompany) return;

      // Update company in Supabase
      const { error } = await supabase
        .from("company")
        .update({
          name: values.name,
          status: values.status,
          country: values.country,
          city: values.city,
          postal_code: values.postal_code,
          vat_number: values.vat_number,
          eori_number: values.eori_number,
          coc_number: values.coc_number,
          article_23: values.article_23,
          phone_number: values.phone_number,
          street: values.street,
          house_number: values.house_number,
          pricing_payment_term: values.pricing_payment_term,
        })
        .eq("id", selectedCompany.id);

      if (error) throw error;

      message.success("Company updated successfully");
      setEditModalVisible(false);

      // Refresh appropriate lists
      fetchCompanies();
      fetchPendingCompanies();
    } catch (error) {
      console.error("Error updating company:", error);
      message.error("Failed to update company");
    }
  };

  // Render status tag with appropriate color
  const renderStatusTag = (status: string | null) => {
    if (!status) return <Tag color="default">Unknown</Tag>;

    switch (status) {
      case "approved":
        return <Tag color="success">Approved</Tag>;
      case "rejected":
        return <Tag color="error">Rejected</Tag>;
      case "unapproved":
        return <Tag color="warning">Unapproved</Tag>;
      case "pending":
        return <Tag color="processing">Pending</Tag>;
      case "deleted":
        return (
          <Tag color="default" style={{ textDecoration: "line-through" }}>
            Deleted
          </Tag>
        );
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  // Handle showing delete confirmation modal
  const showDeleteConfirmation = (company: Company) => {
    setCompanyToDelete(company);
    setDeleteModalVisible(true);
  };

  // Handle confirming deletion
  const confirmDelete = async () => {
    if (!companyToDelete) return;

    await handleStatusChange(companyToDelete, "deleted");
    setDeleteModalVisible(false);
    setCompanyToDelete(null);
  };

  // Handle tab change
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === "pending") {
      fetchPendingCompanies();
    } else {
      fetchCompanies();
    }
  };

  // Handle pending filter change
  const handlePendingFilterChange = (value: string) => {
    setPendingFilter(value);
  };

  // Table columns
  const columns: ColumnsType<Company> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (text) => text,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      sorter: true,
      render: renderStatusTag,
    },
    {
      title: "Picqer",
      key: "picqer_status",
      width: 80,
      align: "center",
      filters: [
        { text: "Connected", value: "connected" },
        { text: "Not Connected", value: "not_connected" },
      ],
      onFilter: (value, record) => {
        const isConnected = Boolean(
          record.picqer_enabled && record.picqer_fulfilment_customer_id
        );
        if (value === "connected") {
          return isConnected;
        } else if (value === "not_connected") {
          return !isConnected;
        }
        return true;
      },
      render: (_, record) => {
        const isConnected =
          record.picqer_enabled && record.picqer_fulfilment_customer_id;
        return (
          <span
            style={{
              fontSize: "16px",
              color: isConnected ? "#52c41a" : "#d9d9d9",
              fontWeight: "bold",
            }}
          >
            {isConnected ? "✓" : "✗"}
          </span>
        );
      },
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      width: 120,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      width: 120,
    },
    {
      title: "VAT Number",
      dataIndex: "vat_number",
      key: "vat_number",
      width: 150,
    },
    {
      title: "EORI Number",
      dataIndex: "eori_number",
      key: "eori_number",
      width: 150,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      width: 170,
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Total Invoices",
      key: "total_invoices",
      width: 120,
      align: "center",
      sorter: true,
      render: (_, record) => {
        const stats = companyInvoiceStats[record.id];
        return stats ? stats.invoiceCount : 0;
      },
    },
    {
      title: "Total Revenue",
      key: "total_revenue",
      width: 150,
      align: "right",
      sorter: true,
      render: (_, record) => {
        const stats = companyInvoiceStats[record.id];
        const revenue = stats ? stats.totalRevenue : 0;
        return (
          <span style={{ color: "#3f8600", fontWeight: "500" }}>
            €{revenue.toFixed(2)}
          </span>
        );
      },
    },
    {
      title: "Outstanding Balance",
      key: "outstanding_balance",
      width: 150,
      align: "right",
      sorter: true,
      render: (_, record) => {
        const stats = companyInvoiceStats[record.id];
        const balance = stats ? stats.outstandingBalance : 0;
        return (
          <span style={{ color: balance > 0 ? "#cf1322" : "#52c41a" }}>
            €{balance.toFixed(2)}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Company">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditCompany(record)}
            />
          </Tooltip>
          <Tooltip title="View Users">
            <Button
              type="text"
              icon={<TeamOutlined />}
              onClick={() => handleViewUsers(record)}
            />
          </Tooltip>
          {record.status !== "deleted" && (
            <Tooltip title="Mark as Deleted">
              <Button
                type="text"
                danger
                icon={<CloseOutlined />}
                onClick={() => showDeleteConfirmation(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Container>
      <AdminNavBar />

      <Title level={2}>Companies Administration</Title>

      {/* Add Dashboard Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <DashboardCard>
            <Statistic
              title="Total Companies"
              value={totalCompaniesCount}
              loading={loading}
            />
          </DashboardCard>
        </Col>
        <Col span={6}>
          <DashboardCard>
            <Statistic
              title="Pending Approval"
              value={
                (statusCounts.find((s) => s.status === "pending")?.count || 0) +
                (statusCounts.find((s) => s.status === "unapproved")?.count ||
                  0)
              }
              valueStyle={{ color: "#faad14" }}
            />
          </DashboardCard>
        </Col>
        <Col span={6}>
          <DashboardCard>
            <Statistic
              title="Approved"
              value={
                statusCounts.find((s) => s.status === "approved")?.count || 0
              }
              valueStyle={{ color: "#52c41a" }}
            />
          </DashboardCard>
        </Col>
        <Col span={6}>
          <Row gutter={16}>
            <Col span={12}>
              <DashboardCard>
                <Statistic
                  title="Rejected"
                  value={
                    statusCounts.find((s) => s.status === "rejected")?.count ||
                    0
                  }
                  valueStyle={{ color: "#f5222d" }}
                />
              </DashboardCard>
            </Col>
            <Col span={12}>
              <DashboardCard>
                <Statistic
                  title="Deleted"
                  value={
                    statusCounts.find((s) => s.status === "deleted")?.count || 0
                  }
                  valueStyle={{
                    color: "#8c8c8c",
                    textDecoration: "line-through",
                  }}
                />
              </DashboardCard>
            </Col>
          </Row>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        style={{ marginBottom: 16 }}
      >
        <TabPane
          tab={
            <Badge count={pendingCompanies.length} offset={[10, 0]} color="red">
              <span>Pending Approval</span>
            </Badge>
          }
          key="pending"
        >
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Segmented
              options={[
                { label: "Pending", value: "pending" },
                { label: "Unapproved", value: "unapproved" },
              ]}
              value={pendingFilter}
              onChange={(value) => handlePendingFilterChange(value as string)}
            />
            <Button
              type="primary"
              onClick={() => fetchPendingCompanies()}
              loading={pendingLoading}
            >
              Refresh Pending Companies
            </Button>
          </div>

          {pendingLoading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              Loading pending companies...
            </div>
          ) : pendingCompanies.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Title level={5} style={{ color: "#8c8c8c" }}>
                No companies pending approval
              </Title>
            </div>
          ) : (
            <Row gutter={[16, 16]}>
              {pendingCompanies.map((company) => (
                <Col xs={24} key={company.id}>
                  <ApprovalCard onClick={() => handleEditCompany(company)}>
                    <div className="company-name">
                      {company.name}
                      <span className="company-id">ID: {company.id}</span>
                    </div>
                    <div className="company-meta">
                      <div className="company-meta-item">
                        <span className="meta-label">Status:</span>
                        {renderStatusTag(company.status)}
                      </div>
                      {company.country && (
                        <div className="company-meta-item">
                          <span className="meta-label">Country:</span>
                          <span>{company.country}</span>
                        </div>
                      )}
                      {company.city && (
                        <div className="company-meta-item">
                          <span className="meta-label">City:</span>
                          <span>{company.city}</span>
                        </div>
                      )}
                      {company.vat_number && (
                        <div className="company-meta-item">
                          <span className="meta-label">VAT Number:</span>
                          <span>{company.vat_number}</span>
                        </div>
                      )}
                      <div className="company-meta-item">
                        <span className="meta-label">Created:</span>
                        <span>
                          {new Date(company.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div
                      className="approval-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        type="text"
                        icon={<TeamOutlined />}
                        onClick={() => handleViewUsers(company)}
                      >
                        View Users
                      </Button>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEditCompany(company)}
                      >
                        Edit
                      </Button>
                      <Button
                        danger
                        icon={<CloseOutlined />}
                        onClick={() => handleStatusChange(company, "rejected")}
                      >
                        Reject
                      </Button>
                      <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={() => handleStatusChange(company, "approved")}
                      >
                        Approve
                      </Button>
                      <Button
                        type="text"
                        danger
                        icon={<CloseOutlined />}
                        onClick={() => showDeleteConfirmation(company)}
                      >
                        Delete
                      </Button>
                    </div>
                  </ApprovalCard>
                </Col>
              ))}
            </Row>
          )}
        </TabPane>

        <TabPane tab="All Companies" key="all">
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Space>
              <Input
                placeholder="Search companies..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 250 }}
              />
              <Button
                type="primary"
                onClick={() => fetchCompanies()}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </div>

          <CompactTable>
            <Table
              className="compact-table"
              columns={columns}
              dataSource={companies}
              rowKey="id"
              pagination={{
                ...pagination,
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} companies`,
              }}
              loading={loading}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
              onRow={(record) => ({
                onClick: () => handleEditCompany(record),
                style: { cursor: "pointer" },
              })}
            />
          </CompactTable>
        </TabPane>
      </Tabs>

      {/* Edit Company Modal */}
      <Modal
        title={`Edit Company: ${selectedCompany?.name || ""} (ID: ${
          selectedCompany?.id || ""
        })`}
        open={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => setEditModalVisible(false)}
        width={900}
        destroyOnClose
        footer={
          selectedCompany?.status === "pending" ||
          selectedCompany?.status === "unapproved"
            ? [
                <Button key="cancel" onClick={() => setEditModalVisible(false)}>
                  Cancel
                </Button>,
                <Button
                  key="reject"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => {
                    setEditModalVisible(false);
                    handleStatusChange(selectedCompany, "rejected");
                  }}
                >
                  Reject
                </Button>,
                <Button
                  key="approve"
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => {
                    setEditModalVisible(false);
                    handleStatusChange(selectedCompany, "approved");
                  }}
                >
                  Approve
                </Button>,
                <Button key="save" type="default" onClick={handleEditSubmit}>
                  Save Changes
                </Button>,
              ]
            : undefined
        }
      >
        {selectedCompany?.moneybird_contact_id && (
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              style={{
                backgroundColor: "#21b36a",
                borderColor: "#21b36a",
                display: "flex",
                alignItems: "center",
              }}
              icon={<ExportOutlined style={{ fontSize: 18, marginRight: 8 }} />}
              href={`https://moneybird.com/443982818600552286/contacts/${selectedCompany.moneybird_contact_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Moneybird
            </Button>
          </div>
        )}
        <Form
          form={form}
          layout="vertical"
          initialValues={selectedCompany || {}}
        >
          <Form.Item
            name="name"
            label="Company Name"
            rules={[{ required: true, message: "Please enter company name" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select a status" }]}
          >
            <Select>
              <Option value="pending">Pending</Option>
              <Option value="unapproved">Unapproved</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
              <Option value="deleted">Deleted</Option>
            </Select>
          </Form.Item>

          <div style={{ display: "flex", gap: 16 }}>
            <Form.Item name="country" label="Country" style={{ flex: 1 }}>
              <Input />
            </Form.Item>

            <Form.Item name="city" label="City" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <Form.Item name="street" label="Street" style={{ flex: 2 }}>
              <Input />
            </Form.Item>

            <Form.Item
              name="house_number"
              label="House Number"
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="postal_code"
              label="Postal Code"
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <Form.Item name="vat_number" label="VAT Number" style={{ flex: 1 }}>
              <Input />
            </Form.Item>

            <Form.Item
              name="eori_number"
              label="EORI Number"
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <Form.Item name="coc_number" label="COC Number" style={{ flex: 1 }}>
              <Input type="number" />
            </Form.Item>

            <Form.Item
              name="phone_number"
              label="Phone Number"
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
          </div>

          <Form.Item name="article_23" label="Article 23" valuePropName="value">
            <Select>
              <Option value={true}>Yes</Option>
              <Option value={false}>No</Option>
              <Option value="unknown">Unknown</Option>
            </Select>
          </Form.Item>

          <Form.Item name="pricing_payment_term" label="Payment Terms">
            <Select placeholder="Select payment terms">
              {paymentTerms.map((term) => (
                <Option key={term.id} value={term.id}>
                  {term.days} day{term.days !== 1 ? "s" : ""},{" "}
                  {term.interest_vat_import_duties}% interest
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>

        {/* Picqer Integration Section */}
        <Divider>Picqer Integration</Divider>
        {selectedCompany && (
          <AdminPicqerIntegrationSection
            company={selectedCompany}
            onRefresh={async () => {
              // Refetch company data to show updated Picqer info
              if (selectedCompany) {
                await fetchCompanies();
                await fetchPendingCompanies();

                // Update the selectedCompany with fresh data
                const { data: freshCompany } = await supabase
                  .from("company")
                  .select("*")
                  .eq("id", selectedCompany.id)
                  .single();

                if (freshCompany) {
                  setSelectedCompany(freshCompany as any);
                }
              }
            }}
          />
        )}

        {/* Company Invoices Section */}
        <Divider>Sales Invoices</Divider>

        {invoicesLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin />
          </div>
        ) : (
          <>
            {/* Invoice Statistics */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Statistic
                  title="Total Revenue"
                  value={invoiceStats.totalRevenue}
                  precision={2}
                  prefix="€"
                  valueStyle={{ color: "#3f8600" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Outstanding Balance"
                  value={invoiceStats.outstandingBalance}
                  precision={2}
                  prefix="€"
                  valueStyle={{
                    color:
                      invoiceStats.outstandingBalance > 0
                        ? "#cf1322"
                        : "#3f8600",
                  }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Invoices"
                  value={invoiceStats.invoiceCount}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
            </Row>

            {/* Invoice Table */}
            <Table
              dataSource={companyInvoices}
              rowKey="id"
              pagination={{ pageSize: 5, showSizeChanger: false }}
              size="small"
              columns={[
                {
                  title: "Invoice Date",
                  dataIndex: "created_at",
                  key: "created_at",
                  render: (date) => new Date(date).toLocaleDateString(),
                  width: 120,
                },
                {
                  title: "Booking",
                  key: "booking",
                  render: (_, record) => {
                    if (!(record as any).booking_id) return "-";

                    const transportType = (record as any).booking
                      ?.transport_type_info
                      ? Array.isArray(
                          (record as any).booking.transport_type_info
                        )
                        ? (record as any).booking.transport_type_info[0]
                            ?.abbreviation || ""
                        : (record as any).booking.transport_type_info
                            .abbreviation || ""
                      : "";

                    return `EFF${(record as any).booking_id}${transportType}`;
                  },
                  width: 120,
                },
                {
                  title: "Status",
                  dataIndex: "moneybird_payment_status",
                  key: "status",
                  render: (status) => {
                    const statusColors = {
                      paid: "success",
                      open: "processing",
                      overdue: "error",
                      partial: "warning",
                      cancelled: "default",
                    };
                    return (
                      <Tag color={(statusColors as any)[status] || "default"}>
                        {status || "pending"}
                      </Tag>
                    );
                  },
                  width: 100,
                },
                {
                  title: "Amount",
                  key: "amount",
                  render: (_, record) => {
                    const total =
                      (record as any).lines?.reduce(
                        (sum: number, line: any) => {
                          return (
                            sum +
                            line.quantity *
                              line.unit_price *
                              (1 + line.vat_percentage / 100)
                          );
                        },
                        0
                      ) || 0;
                    return `€${total.toFixed(2)}`;
                  },
                  width: 100,
                  align: "right",
                },
                {
                  title: "Due Date",
                  dataIndex: "moneybird_due_date",
                  key: "due_date",
                  render: (date) =>
                    date ? new Date(date).toLocaleDateString() : "-",
                  width: 120,
                },
              ]}
              locale={{
                emptyText: (
                  <Empty
                    description="No invoices found for this company"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </>
        )}
      </Modal>

      {/* View Company Users Modal */}
      <Modal
        title={`Users for ${selectedCompany?.name || "Company"}`}
        open={viewUsersModalVisible}
        onCancel={() => setViewUsersModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewUsersModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        <h4>Current Users</h4>
        <Table
          dataSource={companyUsers}
          rowKey="id"
          pagination={false}
          loading={loading}
          columns={[
            {
              title: "Name",
              key: "name",
              render: (_, record) =>
                `${record.user_metadata?.first_name || ""} ${
                  record.user_metadata?.last_name || ""
                }`.trim() || "N/A",
            },
            {
              title: "Email",
              dataIndex: "email",
              key: "email",
            },
            {
              title: "Created",
              dataIndex: "created_at",
              key: "created_at",
              render: (date) => new Date(date).toLocaleString(),
            },
            {
              title: "Actions",
              key: "actions",
              render: (_, record) => (
                <Button
                  danger
                  size="small"
                  onClick={() => handleUnlinkUser(record.id)}
                >
                  Remove
                </Button>
              ),
            },
          ]}
          locale={{
            emptyText: (
              <Empty
                description="No users associated with this company"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />

        <Divider>Link New User</Divider>

        <Space style={{ width: "100%", marginBottom: 16 }}>
          <Input
            placeholder="Search users by name or email..."
            value={searchUserText}
            onChange={(e) => handleUserSearch(e.target.value)}
            style={{ width: 300 }}
          />
          <Select
            placeholder="Select a user to link"
            style={{ width: 300 }}
            loading={usersLoading}
            value={selectedUserId}
            onChange={(value) => setSelectedUserId(value)}
            filterOption={false}
            showSearch={false}
            options={users.map((user) => ({
              value: user.id,
              label: `${user.user_metadata?.first_name || ""} ${
                user.user_metadata?.last_name || ""
              } (${user.email})`.trim(),
            }))}
          />
          <Button
            type="primary"
            onClick={handleLinkUser}
            loading={linkingUser}
            disabled={!selectedUserId}
          >
            Link User
          </Button>
        </Space>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Yes, Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        <p>Are you sure you want to mark this company as deleted?</p>
        {companyToDelete && (
          <p>
            <strong>Company:</strong> {companyToDelete.name} (ID:{" "}
            {companyToDelete.id})
          </p>
        )}
        <p>
          This action can be reversed by changing the company status back in the
          edit form.
        </p>
      </Modal>
    </Container>
  );
};

export default CompaniesAdmin;
